import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Send, Radio, KeyRound, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const EVENTS = [
  "user.created", "user.updated", "user.disabled",
  "userrecord.created", "userrecord.contract_changed", "userrecord.terminated",
  "manager.changed", "site.assigned",
];

export default function WebhooksPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState(null); // {name, key} — shown in dialog after creation

  const { data: webhooks } = useQuery({
    queryKey: ["webhooks"],
    queryFn: async () => (await api.get("/webhooks")).data.data,
  });

  const { data: apiKeys } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => (await api.get("/api-keys")).data.data,
  });

  const toggle = async (id, active) => {
    await api.patch(`/webhooks/${id}`, { active });
    qc.invalidateQueries({ queryKey: ["webhooks"] });
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce webhook ?")) return;
    await api.delete(`/webhooks/${id}`);
    qc.invalidateQueries({ queryKey: ["webhooks"] });
  };
  const test = async (id) => {
    await api.post(`/webhooks/${id}/test`);
    toast.success("Payload de test envoyé — voir Admin & Logs / Webhooks émis");
  };
  const createKey = async () => {
    const name = window.prompt("Nom de la clé API ?");
    if (!name) return;
    const { data } = await api.post("/api-keys", { name });
    setNewKey({ name: data.data.name, key: data.data.key });
    qc.invalidateQueries({ queryKey: ["api-keys"] });
  };
  const revokeKey = async (id) => {
    if (!window.confirm("Révoquer cette clé ?")) return;
    await api.delete(`/api-keys/${id}`);
    qc.invalidateQueries({ queryKey: ["api-keys"] });
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="webhooks-page">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-terracotta-light text-terracotta text-[12px] font-medium mb-3">
          Intégration temps réel
        </div>
        <h1 className="font-heading text-[40px] font-semibold text-warm-dark tracking-tight">API & Webhooks</h1>
        <p className="text-[15px] text-warm-dark/70 mt-1">Clés d'API pour NIM et notifications push sur événements RH.</p>
      </div>

      {/* API docs link */}
      <Card className="border-sand shadow-soft rounded-xl p-5 mb-5 bg-off-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-sage-light flex items-center justify-center">
            <ExternalLink size={16} strokeWidth={1.5} className="text-sage" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-[18px] font-semibold text-warm-dark">Documentation API interactive</div>
            <div className="text-[13px] text-warm-dark/70">Swagger UI auto-généré — testez chaque endpoint NIM-compatible depuis votre navigateur.</div>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`}
            target="_blank"
            rel="noreferrer"
            className="text-terracotta hover:text-terracotta-dark font-medium text-[14px]"
            data-testid="link-swagger"
          >
            Ouvrir /api/docs →
          </a>
        </div>
      </Card>

      {/* API keys */}
      <Card className="border-sand shadow-soft rounded-xl overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-sand bg-cream/50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-warm-dark/60">
            <KeyRound size={12} strokeWidth={1.5} />
            Clés d'API (X-API-Key)
          </div>
          <Button size="sm" onClick={createKey} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="btn-new-apikey">
            <Plus size={13} strokeWidth={1.5} className="mr-1.5" />Nouvelle clé
          </Button>
        </div>
        <div className="divide-y divide-sand">
          {(apiKeys || []).length === 0 && <div className="p-8 text-center text-warm-dark/50 text-[13px]">Aucune clé d'API.</div>}
          {(apiKeys || []).map((k) => (
            <div key={k.id} className="p-4 flex items-center gap-4" data-testid={`apikey-${k.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-warm-dark">{k.name}</div>
                <div className="text-[11px] font-mono text-warm-dark/70 mt-0.5 truncate">{k.key_preview}</div>
                <div className="text-[11px] text-warm-dark/50 mt-0.5">Créée le {new Date(k.created_at).toLocaleDateString("fr-FR")} · {k.usage_count || 0} appel(s)</div>
              </div>
              <button onClick={() => revokeKey(k.id)} className="text-warm-dark/50 hover:text-error p-1.5"><Trash2 size={14} strokeWidth={1.5} /></button>
            </div>
          ))}
        </div>
      </Card>

      {/* Webhooks */}
      <Card className="border-sand shadow-soft rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-sand bg-cream/50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-warm-dark/60">
            <Radio size={12} strokeWidth={1.5} />
            Webhooks sortants
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="btn-new-webhook">
            <Plus size={13} strokeWidth={1.5} className="mr-1.5" />Nouveau webhook
          </Button>
        </div>
        <div className="divide-y divide-sand">
          {(webhooks || []).length === 0 && <div className="p-8 text-center text-warm-dark/50 text-[13px]">Aucun webhook configuré.</div>}
          {(webhooks || []).map((w) => (
            <div key={w.id} className="p-5 flex items-start gap-4" data-testid={`webhook-${w.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-[16px] font-semibold text-warm-dark">{w.name}</div>
                <div className="font-mono text-[12px] text-warm-dark/80 mt-0.5 truncate">{w.url}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(w.events || []).map((ev) => (
                    <span key={ev} className="px-1.5 py-0.5 bg-terracotta-light text-terracotta text-[10px] rounded font-mono">{ev}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={w.active} onCheckedChange={(v) => toggle(w.id, v)} data-testid={`webhook-toggle-${w.id}`} />
                <Button size="sm" variant="outline" onClick={() => test(w.id)} data-testid={`webhook-test-${w.id}`}><Send size={13} strokeWidth={1.5} /></Button>
                <button onClick={() => remove(w.id)} className="text-warm-dark/50 hover:text-error p-1.5"><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <WebhookCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ApiKeyRevealDialog data={newKey} onClose={() => setNewKey(null)} />
    </div>
  );
}

function ApiKeyRevealDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!data?.key) return;
    try {
      await navigator.clipboard.writeText(data.key);
      setCopied(true);
      toast.success("Clé copiée dans le presse-papier");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier — sélectionnez et copiez manuellement");
    }
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-off-white" data-testid="apikey-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-warm-dark">Nouvelle clé d'API créée</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-[13px] text-warm-dark/80">
            Voici la clé pour <span className="font-semibold">« {data?.name} »</span>. Copiez-la
            <span className="font-semibold"> dès maintenant</span> — pour des raisons de sécurité,
            elle ne sera <span className="underline">plus jamais affichée</span> en entier.
          </div>
          <div className="rounded-lg border border-terracotta/40 bg-terracotta-light/30 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-warm-dark/60 mb-2">Clé secrète</div>
            <div className="font-mono text-[14px] text-warm-dark break-all select-all" data-testid="apikey-reveal-value">
              {data?.key}
            </div>
          </div>
          <div className="rounded-md border border-sand bg-cream/40 p-3 text-[11px] text-warm-dark/70">
            <span className="font-semibold">Utilisation :</span> ajoutez l'en-tête HTTP
            <span className="font-mono mx-1">X-API-Key: {data?.key?.slice(0, 12)}…</span>
            sur chaque appel /api/v1/*.
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copy} data-testid="apikey-reveal-copy">
            {copied ? <Check size={14} className="mr-1.5 text-sage" /> : <Copy size={14} className="mr-1.5" />}
            {copied ? "Copié" : "Copier la clé"}
          </Button>
          <Button onClick={onClose} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="apikey-reveal-close">
            J'ai sauvegardé la clé
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebhookCreateDialog({ open, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", url: "", events: ["user.created", "user.updated"], headers: "" });
  const toggleEvent = (ev) => {
    setForm({ ...form, events: form.events.includes(ev) ? form.events.filter((e) => e !== ev) : [...form.events, ev] });
  };
  const submit = async () => {
    if (!form.name || !form.url) return;
    let headers = {};
    try { headers = form.headers ? JSON.parse(form.headers) : {}; } catch { toast.error("Headers: JSON invalide"); return; }
    await api.post("/webhooks", { ...form, headers, active: true });
    toast.success("Webhook créé");
    qc.invalidateQueries({ queryKey: ["webhooks"] });
    onClose();
    setForm({ name: "", url: "", events: ["user.created", "user.updated"], headers: "" });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-off-white" data-testid="webhook-create-dialog">
        <DialogHeader><DialogTitle className="font-heading">Nouveau webhook</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[12px] mb-1.5 block">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wh-name" /></div>
          <div><Label className="text-[12px] mb-1.5 block">URL cible</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://nim.acme.com/api/events" className="font-mono" data-testid="wh-url" /></div>
          <div>
            <Label className="text-[12px] mb-1.5 block">Événements</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {EVENTS.map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-[12px] text-warm-dark cursor-pointer">
                  <Checkbox checked={form.events.includes(ev)} onCheckedChange={() => toggleEvent(ev)} data-testid={`wh-ev-${ev}`} />
                  <span className="font-mono">{ev}</span>
                </label>
              ))}
            </div>
          </div>
          <div><Label className="text-[12px] mb-1.5 block">Headers custom (JSON)</Label><Input value={form.headers} onChange={(e) => setForm({ ...form, headers: e.target.value })} placeholder='{"Authorization": "Bearer xxx"}' className="font-mono text-[12px]" data-testid="wh-headers" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="wh-submit">Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
